import React, { Component, ReactElement, ComponentType } from 'react'

// import React, { useState, useMemo } from 'react';

export interface Crumb {
  title: string;
  path: string;
}

interface BreadcrumbContext {
  crumbs: Crumb[]
  clearCrumb: () => void
  addCrumb: (crumb: Crumb) => void
  removeCrumb: (crumb: Crumb) => void
  replaceCrumb: (oldCrumb: Crumb, newCrumb: Crumb) => void
}

/* istanbul ignore next */
const defaultContext: BreadcrumbContext = {
  crumbs: [],
  // tslint:disable-next-line
  clearCrumb: () => {},
  // tslint:disable-next-line
  addCrumb: (crumb: Crumb) => {},
  // tslint:disable-next-line
  removeCrumb: (crumb: Crumb) => {},
  // tslint:disable-next-line
  replaceCrumb: (oldCrumb: Crumb, newCrumb: Crumb) => {}
}

const BreadContext = React.createContext(defaultContext)

interface Props {
  children: ReactElement<any> | ReactElement<any>[]
}

interface PropsCrumb {
  crumb: Crumb
  addCrumb: (crumb: Crumb) => void
  removeCrumb: (crumb: Crumb) => void
  replaceCrumb: (oldCrumb: Crumb, newCrumb: Crumb) => void
}

interface State {
  crumbs: Crumb[]
}

class BreadcrumbsProvider extends Component<Props, State> {
  state = {
    crumbs: []
  }

  addCrumb = (crumb: Crumb) => {
    // use function version of set state
    // to get accurate tracking between state
    this.setState(({ crumbs: prevCrumbs }) => ({
      crumbs: [...prevCrumbs, crumb]
    }))
  }

  findCrumb = (crumb: Crumb, crumbs: Crumb[]): number | undefined => {
    for (let i = 0; i < crumbs.length; i++) {
      const { title, path } = crumbs[i];
      if (crumb.title === title && crumb.path === path) {
        return i;
      }
    }

    return undefined;
  }

  removeCrumb = (crumbToRemove: Crumb) => {
    this.setState(({ crumbs: prevCrumbs }) => {
      const crumbIndex = this.findCrumb(crumbToRemove, prevCrumbs);

      let crumbs

      if (crumbIndex !== undefined) {
        crumbs = [...prevCrumbs]
        crumbs.splice(crumbIndex, 1)
      } else {
        crumbs = prevCrumbs
      }

      return { crumbs }
    })
  }

  replaceCrumb = (crumbToReplace: Crumb, crumbReplacement: Crumb) => {
    this.setState(({ crumbs: prevCrumbs }) => {
      const crumbIndex = this.findCrumb(crumbToReplace, prevCrumbs);

      let crumbs

      if (crumbIndex !== undefined) {
        crumbs = [...prevCrumbs];
        crumbs.splice(crumbIndex, 1, crumbReplacement);
      } else {
        crumbs = prevCrumbs;
      }

      return { crumbs };
    })
  }

  clearCrumb = () => {
    this.setState(({ crumbs }) => ({
      crumbs: [],
     }))
   }

  render() {
    const { crumbs } = this.state
    const { children } = this.props

    const context = {
      crumbs,
      addCrumb: this.addCrumb,
      removeCrumb: this.removeCrumb,
      replaceCrumb: this.replaceCrumb,
      clearCrumb: this.clearCrumb
    }

    return <BreadContext.Provider value={context}>{children}</BreadContext.Provider>
  }
}

const BreadcrumbsConsumer = BreadContext.Consumer
export { BreadContext, BreadcrumbsProvider, BreadcrumbsConsumer }


class WithBreadcrumbWrapper extends Component<PropsCrumb> {
  constructor(props: PropsCrumb) {
    super(props)

    const { addCrumb, crumb } = props
    addCrumb(crumb)
  }

  componentWillUnmount() {
    const { crumb, removeCrumb } = this.props
    removeCrumb(crumb)
  }

  componentDidUpdate(prevProps: PropsCrumb) {
    const { crumb, replaceCrumb } = this.props
    if (
      prevProps.crumb.title !== crumb.title ||
      prevProps.crumb.path !== crumb.path
    ) {
      replaceCrumb(prevProps.crumb, crumb)
    }
  }

  render() {
    return this.props.children
  }
}

export const withBreadcrumb = <PropsType extends {} = any>(
  crumb: Crumb | ((props: PropsType) => Crumb)
) => (WrappedComponent: ComponentType<PropsType>) => (props: PropsType) => (
  <BreadcrumbsConsumer>
    {({ addCrumb, removeCrumb, replaceCrumb }) => (
      <WithBreadcrumbWrapper
        crumb={typeof crumb === 'function' ? crumb(props) : crumb}
        addCrumb={addCrumb}
        removeCrumb={removeCrumb}
        replaceCrumb={replaceCrumb}
      >
        <WrappedComponent {...props} />
      </WithBreadcrumbWrapper>
    )}
  </BreadcrumbsConsumer>
)